import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { api } from "../lib/api";
import PresenceWidget from "../components/PresenceWidget";
import {
  Users, MapTrifold, Path, MapPin, UsersThree, Leaf, ShieldCheck,
  ArrowUpRight, Gauge, Robot, ChatCircleDots,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { Link } from "react-router-dom";

const stats = [
  { key: "total_customers", label: "Customers", icon: Users },
  { key: "pending_customers", label: "Pending Verification", icon: ShieldCheck, accent: true },
  { key: "locations", label: "Locations", icon: MapTrifold },
  { key: "zones", label: "Health Zones", icon: Path },
  { key: "drop_points", label: "Drop Points", icon: MapPin },
  { key: "staff", label: "Staff & Admins", icon: UsersThree },
  { key: "waste_types", label: "Waste Types", icon: Leaf },
  { key: "audit_events", label: "Audit Events", icon: Gauge },
];

const CHART_COLORS = ["#2D5A27", "#C25D38", "#8A8780"];

export default function Dashboard() {
  const [kpi, setKpi] = useState(null);
  const [audit, setAudit] = useState([]);
  const [aiCoverage, setAiCoverage] = useState(null);
  const [handoffCount, setHandoffCount] = useState(0);
  const me = (() => {
    try { return JSON.parse(localStorage.getItem("rescigo_user") || "null"); }
    catch { return null; }
  })();

  useEffect(() => {
    api.get("/dashboard/kpis").then(r => setKpi(r.data)).catch(() => {});
    api.get("/audit-logs?limit=8").then(r => setAudit(r.data?.rows || [])).catch(() => {});
    api.get("/analytics/ai-coverage?days=7").then(r => setAiCoverage(r.data)).catch(() => {});
    api.get("/crm/handoff-queue/count").then(r => setHandoffCount(r.data?.count || 0)).catch(() => {});
  }, []);

  const barData = kpi ? [
    { name: "Locations", value: kpi.locations },
    { name: "Zones", value: kpi.zones },
    { name: "Drops", value: kpi.drop_points },
    { name: "Customers", value: kpi.total_customers },
    { name: "Staff", value: kpi.staff },
    { name: "Waste Types", value: kpi.waste_types },
  ] : [];

  return (
    <AppShell
      title="Operations Dashboard"
      subtitle="Overview"
      actions={
        <Link
          to="/customers/verify"
          data-testid="btn-verify-queue"
          className="inline-flex items-center gap-2 h-10 px-4 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium transition-colors"
        >
          Verification Queue <ArrowUpRight size={14} weight="bold" />
        </Link>
      }
    >
      <PresenceWidget user={me} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" data-testid="kpi-grid">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="stat-card" data-testid={`kpi-${s.key}`}>
              <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.accent ? "bg-[#C25D38]/10" : "bg-[#2D5A27]/8"}`}>
                  <Icon size={18} color={s.accent ? "#C25D38" : "#2D5A27"} weight="duotone" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Live</div>
              </div>
              <div className="mt-6">
                <div className="font-heading text-3xl tracking-tight text-[#1A1A1A]">
                  {kpi ? kpi[s.key] ?? 0 : "—"}
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mt-1">
                  {s.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Iter 141 — AI Coverage tile + Handoff Queue quick-link */}
      {(aiCoverage || handoffCount > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-testid="ai-coverage-row">
          <div className="md:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-5"
               data-testid="ai-coverage-tile">
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md bg-[#0B2B66]/10 flex items-center justify-center">
                  <Robot size={16} weight="fill" className="text-[#0B2B66]"/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Rigo AI · last 7 days</div>
                  <div className="font-heading text-lg text-[#1A1A1A]">Auto-reply coverage</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl text-[#0B2B66]"
                      data-testid="ai-coverage-rate">
                  {aiCoverage?.resolution_rate ?? 0}%
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Handled by AI</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <MiniStat label="Customer msgs" value={aiCoverage?.total_customer_msgs ?? 0} testid="ai-cov-cust"/>
              <MiniStat label="AI replies" value={aiCoverage?.ai_replies ?? 0} tone="blue" testid="ai-cov-ai"/>
              <MiniStat label="Staff replies" value={aiCoverage?.staff_replies ?? 0} tone="green" testid="ai-cov-staff"/>
            </div>
            {aiCoverage?.series?.length > 0 && (
              <div className="mt-3">
                <ResponsiveContainer width="100%" height={64} minWidth={0} debounce={100}>
                  <LineChart data={aiCoverage.series} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <Line type="monotone" dataKey="ai" stroke="#0B2B66" strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="staff" stroke="#2D5A27" strokeWidth={2} dot={false}/>
                    <XAxis dataKey="day" hide/>
                    <Tooltip
                      contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E2D9",
                                       borderRadius: 6, fontSize: 11 }}
                      formatter={(v, k) => [v, k === "ai" ? "AI" : "Staff"]}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <Link to="/crm/handoff-queue"
                data-testid="handoff-tile"
                className={`block bg-white border rounded-md p-5 transition-colors ${
                  handoffCount > 0
                    ? "border-[#C25D38] bg-[#F9EDE7] hover:bg-[#F4C7B9]"
                    : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                }`}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                handoffCount > 0 ? "bg-[#C25D38]/20" : "bg-[#8A8780]/10"
              }`}>
                <ChatCircleDots size={16} weight="fill"
                                 className={handoffCount > 0 ? "text-[#7A2E18]" : "text-[#5C5A55]"}/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Safety flagged</div>
                <div className="font-heading text-lg text-[#1A1A1A]">Handoff queue</div>
              </div>
            </div>
            <div className="font-heading text-4xl mt-2"
                 data-testid="handoff-count"
                 style={{ color: handoffCount > 0 ? "#7A2E18" : "#5C5A55" }}>
              {handoffCount}
            </div>
            <div className="text-[11px] text-[#5C5A55] mt-1">
              {handoffCount === 0
                ? "No chats need senior triage right now."
                : `${handoffCount} chat${handoffCount > 1 ? "s" : ""} awaiting senior triage`}
            </div>
            <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A2E18]">
              Open queue <ArrowUpRight size={12} weight="bold"/>
            </div>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Master data</div>
              <div className="font-heading text-lg text-[#1A1A1A]">System configuration counts</div>
            </div>
          </div>
          <div className="h-72 min-h-[288px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                <XAxis dataKey="name" stroke="#5C5A55" fontSize={12} />
                <YAxis stroke="#5C5A55" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: "#FFFFFF", border: "1px solid #E5E2D9",
                    borderRadius: 6, fontSize: 12,
                  }}
                />
                <Bar dataKey="value" fill="#2D5A27" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Customers by status</div>
          <div className="font-heading text-lg text-[#1A1A1A] mb-2">Verification breakdown</div>
          <div className="h-56 min-h-[224px]">
            {kpi && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={kpi.customers_by_status.length ? kpi.customers_by_status : [{name:"No data",value:1}]}
                    innerRadius={40}
                    outerRadius={75}
                    dataKey="value"
                  >
                    {(kpi.customers_by_status.length ? kpi.customers_by_status : [{name:"No data",value:1}]).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E2D9", borderRadius: 6, fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {kpi?.customers_by_status?.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-xs text-[#5C5A55]">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                {s.name}: <span className="font-medium text-[#1A1A1A]">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[#E5E2D9] rounded-md">
        <div className="p-6 border-b border-[#E5E2D9] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Recent activity</div>
            <div className="font-heading text-lg text-[#1A1A1A]">Audit trail</div>
          </div>
          <Link to="/audit-logs" className="text-xs text-[#2D5A27] hover:underline">View all →</Link>
        </div>
        <div className="divide-y divide-[#E5E2D9]">
          {audit.length === 0 && <div className="p-6 text-sm text-[#8A8780]">No activity yet.</div>}
          {audit.map((e) => (
            <div key={e.id} className="px-6 py-3 flex items-center gap-4 text-sm" data-testid="audit-row">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A27]" />
              <div className="font-mono text-[11px] text-[#8A8780] w-44 truncate">
                {new Date(e.timestamp).toLocaleString()}
              </div>
              <div className="text-[#1A1A1A] font-medium w-48 truncate">{e.action}</div>
              <div className="text-[#5C5A55] flex-1 truncate">by {e.actor}</div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function MiniStat({ label, value, tone = "default", testid }) {
  const color = tone === "blue" ? "text-[#0B2B66]"
             : tone === "green" ? "text-[#1A3317]" : "text-[#3A3A38]";
  return (
    <div className="bg-[#F7F6F2] rounded p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
      <div className={`font-heading text-lg font-semibold ${color}`}>{value?.toLocaleString?.("en-IN") ?? value}</div>
    </div>
  );
}

